import { effect, Injectable, Signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { isNumber, sortBy, uniqueId } from 'lodash';
import { Observable } from 'rxjs';
import {
    EventInfo,
    EventQuery,
    EventQueryRule,
    IqEventBusNode,
    IqEventBusQueueState,
    matchQuery,
    matchRule,
} from './iq-event-bus.models';

@Injectable()
export class IqEventBusQueueService extends ComponentStore<IqEventBusQueueState> {
    private MAX_EVENTS = 300;

    get maxEvents(): number {
        return this.MAX_EVENTS;
    }

    set maxEvents(value: number) {
        this.MAX_EVENTS = value;
    }

    constructor() {
        super({ head: null, tail: null, offsets: {} });

        /**
         * Automatically remove the oldest event when the total number of events exceeds the maximum limit.
         * This is to prevent the queue from growing indefinitely.
         *
         * Reasoning: With the addition of things like GeoJSON and other data that
         * can grow really huge, we should clean stuff up.
         */
        effect(() => {
            /**
             * We only want to remove events that have been handled.
             * This is to prevent removing events that are still being processed.
             */
            const events = this.filteredEvents({
                handled: { value: true, operator: 'eq' },
            })();

            /**
             * Get the total number of handled events.
             */
            const totalEvents = events.length;

            /**
             * Keep this for debugging purposes.
             */
            // console.log('Total events:', totalEvents);
            // console.log('Events:', events);
            // console.log('Offsets:', this.state().offsets);

            if (totalEvents <= this.MAX_EVENTS) return;

            untracked(() => {
                this.removeOldestEvent();
            });
        });
    }

    //#region Selectors

    readonly filteredEvents$ = (
        query: EventQuery = null,
    ): Observable<EventInfo[]> => {
        return this.select((state: IqEventBusQueueState): EventInfo[] => {
            if (!query) {
                return this._getAllEvents(state);
            }

            return this._getAllEvents(state).filter(
                (evt: EventInfo): boolean => {
                    return this._matchQuery(evt, query);
                },
            );
        });
    };
    readonly filteredEvents = (
        query: EventQuery = null,
    ): Signal<EventInfo[]> => {
        return this.selectSignal((state: IqEventBusQueueState): EventInfo[] => {
            if (!query) {
                return this._getAllEvents(state);
            }

            return this._getAllEvents(state).filter(
                (evt: EventInfo): boolean => {
                    return this._matchQuery(evt, query);
                },
            );
        });
    };

    readonly isAllHandled: Signal<boolean> = toSignal(
        this.select(
            this.filteredEvents$({ handled: { value: false, operator: 'eq' } }),
            (events: EventInfo[]): boolean => {
                return events.length <= 0;
            },
        ),
    );

    readonly unhandledEvents: Signal<EventInfo[]> = toSignal(
        this.select(
            this.filteredEvents$({ handled: { value: false, operator: 'eq' } }),
            (events: EventInfo[]): EventInfo[] => events,
        ),
    );

    readonly eventNodes$ = (
        query: EventQuery = null,
    ): Observable<IqEventBusNode[]> => {
        return this.select((state: IqEventBusQueueState): IqEventBusNode[] => {
            if (!query) {
                return this._getAllEventNodes(state);
            }

            return this._getAllEventNodes(state).filter(
                (node: IqEventBusNode): boolean => {
                    return this._matchQuery(node.evt, query);
                },
            );
        });
    };
    readonly filteredEventNodes = (
        query: EventQuery = null,
    ): Signal<IqEventBusNode[]> => {
        return this.selectSignal(
            (state: IqEventBusQueueState): IqEventBusNode[] => {
                if (!query) {
                    return this._getAllEventNodes(state);
                }

                return this._getAllEventNodes(state).filter(
                    (node: IqEventBusNode): boolean => {
                        return this._matchQuery(node.evt, query);
                    },
                );
            },
        );
    };

    readonly unhandledEventNodes: Signal<IqEventBusNode[]> = toSignal(
        this.select(
            this.eventNodes$({ handled: { value: false, operator: 'eq' } }),
            (events: IqEventBusNode[]): IqEventBusNode[] => events,
        ),
    );
    readonly unhandledEventNodeMap: Signal<Map<string, IqEventBusNode[]>> =
        toSignal(
            this.select(
                this.eventNodes$({ handled: { value: false, operator: 'eq' } }),
                (events: IqEventBusNode[]): Map<string, IqEventBusNode[]> => {
                    const map = new Map<string, IqEventBusNode[]>();

                    return events.reduce(
                        (
                            acc: Map<string, IqEventBusNode[]>,
                            eventNode: IqEventBusNode,
                        ): Map<string, IqEventBusNode[]> => {
                            const topic: string = eventNode.topic;
                            const list: IqEventBusNode[] = acc.get(topic)
                                ? [...acc.get(topic), eventNode]
                                : [eventNode];
                            const sortedList: IqEventBusNode[] = sortBy(
                                list,
                                'offset',
                            );

                            return acc.set(topic, sortedList);
                        },
                        map,
                    );
                },
            ),
        );

    readonly allEvents: Signal<EventInfo[]> = toSignal(
        this.select(
            this.filteredEvents$(),
            (events: EventInfo[]): EventInfo[] => events,
        ),
    );

    readonly eventNodes: Signal<IqEventBusNode[]> = this.selectSignal(
        (state: IqEventBusQueueState): IqEventBusNode[] =>
            this._getAllEventNodes(state),
    );

    //#endregion

    //#region Updaters

    readonly addEvents = this.effect((events$: Observable<EventInfo[]>) => {
        return events$.pipe(
            tapResponse({
                next: (evts: EventInfo[]) => {
                    this.addValidEvents(evts);
                },
                error: (err) => {
                    console.error('Error adding events', err);
                },
            }),
        );
    });
    readonly addValidEvents = this.updater(
        (state: IqEventBusQueueState, evts: EventInfo[]) => {
            return evts.reduce((acc, evt, index) => {
                return {
                    ...acc,
                    ...this._appendEvent(
                        acc.head ?? null,
                        acc.tail ?? null,
                        acc.offsets,
                        evt,
                    ),
                };
            }, state);
        },
    );

    //test

    readonly addEvent = this.updater(
        (state: IqEventBusQueueState, evt: EventInfo) => {
            const newState: IqEventBusQueueState = {
                ...state,
                ...this._appendEvent(
                    state.head ?? null,
                    state.tail ?? null,
                    state.offsets,
                    evt,
                ),
            };
            
            return newState;
        },
    );

    readonly addCacheEvent = this.updater(
        (
            state: IqEventBusQueueState,
            { evt, cacheId }: { cacheId: string; evt: EventInfo },
        ) => {
            const newState: IqEventBusQueueState = {
                ...state,
                ...this._appendEvent(
                    state.head ?? null,
                    state.tail ?? null,
                    state.offsets,
                    evt,
                    cacheId,
                ),
            };

            return newState;
        },
    );

    readonly handleEvents = this.updater(
        (state: IqEventBusQueueState): IqEventBusQueueState => {
            let currentEvent = state.head;

            while (currentEvent) {
                const newEvent = {
                    ...currentEvent.evt,
                    handled: true,
                    fired: Date.now(),
                };
                currentEvent.evt = newEvent;
                currentEvent = currentEvent.nextEvent;
            }

            return state;
        },
    );
    readonly handleEvent = this.updater(
        (
            state: IqEventBusQueueState,
            node: IqEventBusNode,
        ): IqEventBusQueueState => {
            let currentEvent: IqEventBusNode = state.head;

            while (currentEvent) {
                const matches: boolean = currentEvent.id === node.id;
                const handled: boolean = matches
                    ? true
                    : currentEvent.evt.handled;
                const fired: number =
                    matches && !currentEvent.evt.handled
                        ? Date.now()
                        : currentEvent.evt.fired;
                const newEvent: EventInfo = {
                    ...currentEvent.evt,
                    handled,
                    fired,
                };

                currentEvent.evt = { ...newEvent };
                currentEvent = currentEvent.nextEvent;
            }

            return state;
        },
    );

    //#endregion

    start() {
        this.setState({
            head: null,
            tail: null,
            offsets: {},
        });
    }

    private _getAllEvents(state: IqEventBusQueueState) {
        let events: EventInfo[] = [];

        if (state.head) {
            let currentEvent = state.head;

            while (currentEvent) {
                events = [...events, currentEvent.evt];
                currentEvent = currentEvent.nextEvent;
            }
        }

        return events;
    }
    private _getAllEventNodes(state: IqEventBusQueueState) {
        let events: IqEventBusNode[] = [];

        if (state?.head) {
            let currentEvent = state.head;

            while (currentEvent) {
                events = [...events, currentEvent];
                currentEvent = currentEvent.nextEvent;
            }
        }

        return events;
    }

    private _appendEvent(
        head: IqEventBusNode,
        tail: IqEventBusNode,
        offsets: Record<string, number>,
        evt: EventInfo,
        id: string = 'event-queue',
    ): Partial<IqEventBusQueueState> {
        const updatedEvent: IqEventBusNode = this._createNewEvent(
            evt,
            offsets,
            id,
        );
        const newOffsets = {
            ...offsets,
            [evt.name]: updatedEvent.offset,
        };

        if (!head) {
            return {
                head: updatedEvent,
                tail: updatedEvent,
                offsets: newOffsets,
            };
        }

        tail.nextEvent = updatedEvent;
        tail = updatedEvent;

        return {
            tail,
            offsets: newOffsets,
        };
    }
    removeOldestEvent(): IqEventBusQueueState {
        const newState: IqEventBusQueueState = this.state();
        const head = newState.head;

        if (!head) {
            return { ...newState };
        }

        head.nextEvent
            ? this.patchState({
                  head: head.nextEvent,
              })
            : this.patchState({
                  head: null,
                  tail: null,
              });

        return this.state();
    }

    private _createNewEvent(
        evt: EventInfo,
        offsets: Record<string, number>,
        id: string = 'event-queue',
    ): IqEventBusNode {
        return {
            id: uniqueId(id + ':'),
            topic: evt.name,
            offset: isNumber(offsets[evt.name]) ? offsets[evt.name] + 1 : 0,
            evt: { ...evt, handled: false, registered: Date.now() },
            nextEvent: null,
        } as IqEventBusNode;
    }

    private _matchQuery(evt: EventInfo, query: EventQuery): boolean {
        return matchQuery(evt, query);
    }

    matchRule(eventProperty: any, queryObj: EventQueryRule): boolean {
        return matchRule(eventProperty, queryObj);
    }
}
